import { useMemo, useState } from 'react';
import {
  Modal, Stack, TextInput, ScrollArea, Group, Text, Button, Radio, Badge, Box, SegmentedControl,
  Center, Loader, Anchor,
} from '@mantine/core';
import { IconSearch, IconPlus, IconArrowLeft } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { MediaStateType } from '@lib2/model/media/MediaTypes';
import { usePlaylists } from '../usePlaylists';
import { useAddMediaToPlaylist, useCreateCustomPlaylist } from '../usePlaylistMutations';
import { PlaylistUtils } from '../PlaylistUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  ADD_TO_PLAYLIST_MAX_OPTIONS,
  PLAYLIST_ADD_POSITIONS,
  PLAYLIST_ADD_POSITION_LABELS,
  PLAYLIST_CONSTRAINTS,
  type PlaylistAddPosition,
} from '@/lib/constants/playlist';
import type { MediaType } from '@/lib/types/Media';
import type { CustomPlaylistView } from '@/lib/types/Playlist';

const ADD_POSITION_OPTIONS = PLAYLIST_ADD_POSITIONS.map(value => ({
  value,
  label: PLAYLIST_ADD_POSITION_LABELS[value],
}));

export interface AddToPlaylistItem {
  mediaId: number;
  title: string;
}

interface AddToPlaylistModalProps {
  opened: boolean;
  onClose: () => void;
  /** Grid media kind — only manual playlists of this kind are offered. */
  mediaType: MediaType;
  /** The selected media (toolbar selection or a single row). */
  items: AddToPlaylistItem[];
  /** Called after items were added (the parent clears its grid selection). */
  onAdded?: () => void;
}

/**
 * "Add to Playlist" from the Videos/Streams grids: pick one manual playlist of
 * the matching media type (or create one inline) and append the selection.
 * Persists immediately — the user is not editing the playlist, so there is
 * no save bar; the playlist page stays the place for ordering.
 *
 * Mount conditionally (`{open && <AddToPlaylistModal/>}`) so state resets per use.
 */
export function AddToPlaylistModal({ opened, onClose, mediaType, items, onAdded }: AddToPlaylistModalProps) {
  const navigate = useNavigate();
  const { selectedCId } = useAuth();
  const { data, isLoading: playlistsLoading } = usePlaylists();
  const addMutation = useAddMediaToPlaylist();
  const createMutation = useCreateCustomPlaylist();
  const noun = PlaylistUtils.getMediaNoun(mediaType);

  const [searchText, setSearchText] = useState('');
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [addPosition, setAddPosition] = useState<PlaylistAddPosition>('end');
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const mediaIds = useMemo(() => items.map(i => i.mediaId), [items]);

  // Manual playlists of this media kind, A-Z (smart playlists are tag-driven
  // and never appear here — the two concepts stay distinct).
  const candidates = useMemo<CustomPlaylistView[]>(() => {
    const lists = (data?.customLists ?? []).filter(l => l.mediaType === mediaType);
    return [...lists].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
  }, [data, mediaType]);

  const { visible, totalMatching } = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const matching = q ? candidates.filter(l => l.title.toLowerCase().includes(q)) : candidates;
    return { visible: matching.slice(0, ADD_TO_PLAYLIST_MAX_OPTIONS), totalMatching: matching.length };
  }, [candidates, searchText]);

  const selected = useMemo(
    () => (selectedListId != null ? candidates.find(l => l.listId === selectedListId) ?? null : null),
    [candidates, selectedListId]
  );

  // Duplicate preview from the cached list (the mutation re-checks against a
  // fresh fetch before writing, so this only steers the button and the hint).
  const alreadyInCount = useMemo(() => {
    if (!selected) return 0;
    const present = new Set(selected.mediaPositions.map(p => p.mediaId));
    return mediaIds.filter(id => present.has(id)).length;
  }, [selected, mediaIds]);
  const toAddCount = items.length - alreadyInCount;

  const hasExisting = candidates.length > 0;
  const showNewForm = creating || (!playlistsLoading && !hasExisting);
  const isPending = addMutation.isPending || createMutation.isPending;
  const trimmedTitle = newTitle.trim();

  const countLabel = (n: number) => `${n} ${n === 1 ? noun.singular : noun.plural}`;
  const intro = items.length === 1
    ? <>Add &ldquo;{items[0].title}&rdquo; to a manual playlist.</>
    : <>Add {countLabel(items.length)} to a manual playlist.</>;

  function notifyAdded(playlist: CustomPlaylistView, addedCount: number, skippedCount: number) {
    const id = `playlist-add-${playlist.listId}-${Date.now()}`;
    const displayId = bcLong.getStr(playlist.listId);
    notifications.show({
      id,
      title: 'Added to playlist',
      color: 'green',
      autoClose: 8000,
      message: (
        <Text size="sm">
          {countLabel(addedCount)} added to &ldquo;{playlist.title}&rdquo;.
          {skippedCount > 0 && ` ${skippedCount} already there.`}{' '}
          <Anchor
            size="sm"
            onClick={() => {
              notifications.hide(id);
              navigate({ to: '/playlists/$id', params: { id: displayId } });
            }}
          >
            Open playlist
          </Anchor>
        </Text>
      ),
    });
  }

  function addTo(listId: number) {
    addMutation.mutate({ listId, mediaIds, addPosition }, {
      onSuccess: ({ playlist, addedCount, skippedCount }) => {
        if (addedCount === 0) {
          notifications.show({
            title: 'Already in playlist',
            message: `${items.length === 1 ? 'It is' : 'They are'} already in “${playlist.title}”.`,
            color: 'blue',
          });
          onClose();
          return;
        }
        notifyAdded(playlist, addedCount, skippedCount);
        onAdded?.();
        onClose();
      },
      onError: (err) => {
        // Keep the modal open — adding again is safe (duplicates are skipped).
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }

  async function createAndAdd() {
    if (!selectedCId || !trimmedTitle) return;
    let created: { listId: number };
    try {
      created = await createMutation.mutateAsync({
        mediaState: MediaStateType.Active,
        mediaType,
        cId: selectedCId,
        title: trimmedTitle,
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create playlist',
        color: 'red',
      });
      return;
    }
    // The playlist now exists: switch to it so a retry after an add failure
    // adds to it instead of creating a second one.
    setSelectedListId(created.listId);
    setCreating(false);
    addTo(created.listId);
  }

  function handleSubmit() {
    if (isPending) return;
    if (showNewForm) {
      void createAndAdd();
    } else if (selectedListId != null && toAddCount > 0) {
      addTo(selectedListId);
    }
  }

  const submitDisabled = showNewForm
    ? trimmedTitle.length === 0
    : selectedListId == null || toAddCount <= 0;

  return (
    <Modal opened={opened} onClose={onClose} title="Add to Playlist" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">{intro}</Text>

        {showNewForm ? (
          <Stack gap="sm">
            {!hasExisting && !playlistsLoading && (
              <Text size="sm">
                You don&rsquo;t have a manual {noun.singular} playlist yet. Create one and the selected {noun.plural} become its first items.
              </Text>
            )}
            <TextInput
              label="New playlist title"
              placeholder="Enter playlist title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
              maxLength={PLAYLIST_CONSTRAINTS.TITLE_MAX}
              description={`${newTitle.length}/${PLAYLIST_CONSTRAINTS.TITLE_MAX}`}
              data-autofocus
            />
            {hasExisting && (
              <Anchor size="sm" onClick={() => setCreating(false)}>
                <IconArrowLeft size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Choose an existing playlist instead
              </Anchor>
            )}
          </Stack>
        ) : (
          <Stack gap="sm">
            <TextInput
              placeholder="Search playlists..."
              leftSection={<IconSearch size={16} />}
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
              data-autofocus
            />

            {totalMatching > ADD_TO_PLAYLIST_MAX_OPTIONS && (
              <Text size="xs" c="dimmed">
                Showing first {ADD_TO_PLAYLIST_MAX_OPTIONS} of {totalMatching} playlists. Refine your search to see more.
              </Text>
            )}

            <ScrollArea h={280} type="auto">
              {playlistsLoading ? (
                <Center py="xl">
                  <Loader size="sm" />
                </Center>
              ) : visible.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No playlists match &ldquo;{searchText.trim()}&rdquo;.
                </Text>
              ) : (
                <Stack gap={0}>
                  {visible.map(list => {
                    const isSelected = list.listId === selectedListId;
                    const isActive = list.mediaStateType === MediaStateType.Active;
                    const count = list.mediaPositions.length;
                    return (
                      <Box
                        key={list.listId}
                        px="sm"
                        py="xs"
                        onClick={() => setSelectedListId(list.listId)}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--mantine-color-gray-2)',
                          backgroundColor: isSelected ? 'var(--mantine-primary-color-light)' : undefined,
                        }}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <Radio
                            checked={isSelected}
                            onChange={() => setSelectedListId(list.listId)}
                            onClick={(e) => e.stopPropagation()}
                            tabIndex={-1}
                            aria-label={list.title}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" truncate="end">{list.title}</Text>
                            <Text size="xs" c="dimmed">{countLabel(count)}</Text>
                          </div>
                          {!isActive && (
                            <Badge size="xs" variant="light" color="gray">Inactive</Badge>
                          )}
                        </Group>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </ScrollArea>

            <Group justify="space-between" align="center">
              <Text size="xs" c="dimmed">
                {selected && alreadyInCount > 0 && (
                  toAddCount > 0
                    ? `${alreadyInCount} of ${items.length} already in this playlist; ${toAddCount} will be added.`
                    : `Already in this playlist.`
                )}
              </Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setCreating(true)}
              >
                New playlist
              </Button>
            </Group>
          </Stack>
        )}

        <Group justify="space-between">
          <Group gap="xs">
            {!showNewForm && (
              <>
                <Text size="sm" c="dimmed">Add to:</Text>
                <SegmentedControl
                  size="xs"
                  value={addPosition}
                  onChange={(value) => setAddPosition(value as PlaylistAddPosition)}
                  data={ADD_POSITION_OPTIONS}
                />
              </>
            )}
          </Group>
          <Group gap="sm">
            <Button variant="default" onClick={onClose} disabled={isPending}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitDisabled} loading={isPending}>
              {showNewForm ? 'Create & Add' : 'Add to Playlist'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
